import {useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Image, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX, resolveOptionSwatchThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {resolveOptionProductName} from '@amblique/king-living/app/utils/product-utils';

/**
 * Single swatch card for option step swatch grid layout.
 * Figma: small square card — image fills top area, centered label below.
 * Used when option products have catalog name only (no shortDescription) and no extra price.
 *
 * When `onViewDetails` is provided, a per-card "See details" link is rendered below
 * the label. Clicking it does NOT select the card — only opens the details overlay.
 */
export function SwatchOptionCard({isDisabled, isOutOfStock = false, isSelected, onSelect, onViewDetails, product}) {
  const styles = useMultiStyleConfig('SwatchOptionCard', {isDisabled, isSelected});
  // Same canonical thumbnail resolution as RadioOptionContent so swatch and radio
  // layouts behave identically across every simple product variant — including
  // those that only carry `c_pimlyConfigurationImageSwatch` and have empty
  // `imageGroups`. Single source of truth for "how to find a product thumbnail".
  const imageUrl = resolveOptionSwatchThumbnailUrl(product);
  const label = resolveOptionProductName(product) || product.name;

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onSelect(product);
    }
  }, [isDisabled, onSelect, product]);

  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
      e.preventDefault();
      onSelect(product);
    }
  }, [isDisabled, onSelect, product]);

  const handleViewDetails = useCallback((e) => {
    e.stopPropagation();
    onViewDetails?.(product);
  }, [onViewDetails, product]);

  const handleViewDetailsKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onViewDetails?.(product);
    }
  }, [onViewDetails, product]);

  return (
    <Box
      {...styles.root}
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      className="swatch-option-card"
      opacity={isOutOfStock ? 0.5 : 1}
      position="relative"
      role="radio"
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Image area */}
      <Box {...styles.imageArea} className="swatch-option-card__image-area" position="relative">
        {imageUrl ? (
          <Image
            {...styles.image}
            alt={product.name}
            className="swatch-option-card__image"
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            loading="lazy"
            src={imageUrl}
          />
        ) : (
          <Box {...styles.imagePlaceholder} className="swatch-option-card__image-placeholder" />
        )}
        {isOutOfStock && (
          <Box
            aria-hidden
            background="linear-gradient(to top right, transparent calc(50% - 1px), rgba(255,0,0,0.5) 50%, transparent calc(50% + 1px))"
            className="swatch-option-card__oos-overlay"
            inset={0}
            pointerEvents="none"
            position="absolute"
          />
        )}
      </Box>

      {/* Label */}
      <Box {...styles.labelArea} className="swatch-option-card__label-area">
        <Text {...styles.label} className="swatch-option-card__label" noOfLines={2}>
          {label}
        </Text>
        {isOutOfStock && (
          <Text className="swatch-option-card__oos-label" color="red.500" fontSize="xs">
            <FormattedMessage
              defaultMessage="Sold out"
              id="customize_panel.swatch_card.sold_out"
            />
          </Text>
        )}
        {onViewDetails && (
          <Text
            as="span"
            className="swatch-option-card__view-details"
            role="button"
            tabIndex={0}
            {...styles.viewDetails}
            onClick={handleViewDetails}
            onKeyDown={handleViewDetailsKeyDown}
          >
            <FormattedMessage
              defaultMessage="See details"
              id="customize_panel.swatch_card.see_details"
            />
          </Text>
        )}
      </Box>
    </Box>
  );
}

SwatchOptionCard.displayName = 'SwatchOptionCard';

SwatchOptionCard.propTypes = {
  isDisabled: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
};
